import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Warehouse, Save, X } from "lucide-react";
import { toast } from "sonner";

export default function StockSettings() {
  const [stores, setStores] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/stocks/negative-stock-auth");
      setStores(r.data.stores || []);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id, v) => setStores(ss => ss.map(s => s.id === id ? { ...s, allow_negative: v } : s));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/stocks/negative-stock-auth", {
        store_flags: Object.fromEntries(stores.map(s => [s.id, !!s.allow_negative])),
      });
      toast.success("Stock settings saved");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const cancel = async () => { await load(); toast.info("Changes discarded"); };

  if (!stores) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="settings-stock-page">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Warehouse className="w-3 h-3"/> Settings</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter" data-testid="settings-stock-title">Stock Settings</h1>
        <div className="text-xs text-muted-foreground mt-2">Store Wise Negative Stock Authorisation — allow or disallow billing below zero stock per store (Owner / Manager only).</div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="neg-auth-card">
        <div className="bg-secondary px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex justify-between">
          <span>Store</span><span>Negative Stock Sale</span>
        </div>
        {stores.length === 0 && <div className="p-6 text-center text-muted-foreground text-sm" data-testid="neg-auth-empty">No stores found — add stores in the Store Master first.</div>}
        {stores.map(s => (
          <div key={s.id} className="flex items-center justify-between px-4 py-3 border-t border-border hover:bg-secondary/40" data-testid={`neg-auth-row-${s.id}`}>
            <div>
              <div className="text-sm font-semibold">{s.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.store_type}{s.status !== "Active" ? " · Inactive" : ""}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-[10px] uppercase tracking-wider ${s.allow_negative ? "text-primary" : "text-muted-foreground"}`} data-testid={`neg-auth-label-${s.id}`}>
                {s.allow_negative ? "Allowed" : "Disallowed"}
              </span>
              <Switch data-testid={`neg-auth-toggle-${s.id}`} checked={!!s.allow_negative} onCheckedChange={(v) => toggle(s.id, v)}/>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-border pt-4 mt-6 flex gap-2">
        <Button data-testid="stock-settings-save" onClick={save} disabled={saving} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]"><Save className="w-3 h-3 mr-1"/>{saving ? "Saving…" : "Save"}</Button>
        <Button data-testid="stock-settings-cancel" variant="outline" onClick={cancel} className="rounded-sm uppercase tracking-[0.15em]"><X className="w-3 h-3 mr-1"/> Cancel</Button>
      </div>
    </div>
  );
}
